'use client';

// module imports
import { questionTypeAndCategory } from '@/constants';
import { IQuestion } from '@/types';

interface NewQuestionFormProps {
  showNewQuestionInput: boolean;
  selectedQuestionType: string;
  newQuestionLabel: string;
  newQuestionDescription: string;
  questions: IQuestion[];
  onQuestionTypeChange: (type: string) => void;
  onQuestionLabelChange: (label: string) => void;
  onQuestionDescriptionChange: (description: string) => void;
  onAddQuestion: () => void;
  onCancel: () => void;
  getQuestionTypeLabel: (type: string) => string;
}

export default function NewQuestionForm({ showNewQuestionInput, selectedQuestionType, newQuestionLabel, newQuestionDescription, questions, onQuestionTypeChange, onQuestionLabelChange, onQuestionDescriptionChange, onAddQuestion, onCancel, getQuestionTypeLabel }: NewQuestionFormProps) {
  if (!showNewQuestionInput) return null;

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">{selectedQuestionType ? getQuestionTypeLabel(selectedQuestionType) : 'Select Type'}</span>
          <h3 className="text-lg font-medium text-gray-800">Add New Question</h3>
        </div>
        <div className="text-sm text-gray-500">{questions.length}/20 questions used</div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Question Type *</label>
          <select value={selectedQuestionType} onChange={(e) => onQuestionTypeChange(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-blue-500 text-sm cursor-pointer">
            <option value="">Select a question type</option>
            {questionTypeAndCategory.map((questionType, index) => (
              <option key={`${questionType.type}-${index}`} value={questionType.type}>
                {questionType.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Label *</label>
          <input type="text" value={newQuestionLabel} onChange={(e) => onQuestionLabelChange(e.target.value)} placeholder="Enter question label" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-blue-500 text-sm" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
          <textarea value={newQuestionDescription} onChange={(e) => onQuestionDescriptionChange(e.target.value)} placeholder="Enter question description" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-blue-500 text-sm resize-none" />
        </div>

        <div className="flex gap-3">
          <button onClick={onAddQuestion} disabled={!newQuestionLabel.trim() || !selectedQuestionType || questions.length >= 20} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium cursor-pointer" title={questions.length >= 20 ? 'You have reached the maximum limit of 20 questions' : ''}>
            {questions.length >= 20 ? 'Limit Reached' : 'Add'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium cursor-pointer">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
